import { WorkoutDataPoint } from '../types/workout';

interface OfflineWorkout {
  id: string;
  workoutData: WorkoutDataPoint[];
  startTime: number;
  totalDuration: number;
  workoutName?: string;
  timestamp: number;
}

const OFFLINE_WORKOUTS_KEY = 'offline_workouts';

export class OfflineStorage {
  // Store workout for later upload
  static storeWorkout(
    workoutData: WorkoutDataPoint[],
    startTime: number,
    totalDuration: number,
    workoutName?: string
  ): string {
    const workoutId = `workout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const workout: OfflineWorkout = {
      id: workoutId,
      workoutData,
      startTime,
      totalDuration,
      workoutName,
      timestamp: Date.now(),
    };

    const stored = this.getStoredWorkouts();
    stored.push(workout);
    localStorage.setItem(OFFLINE_WORKOUTS_KEY, JSON.stringify(stored));

    return workoutId;
  }

  // Get all stored workouts
  static getStoredWorkouts(): OfflineWorkout[] {
    const stored = localStorage.getItem(OFFLINE_WORKOUTS_KEY);
    if (!stored) return [];

    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  // Remove a workout after successful upload
  static removeWorkout(workoutId: string): void {
    const stored = this.getStoredWorkouts();
    const filtered = stored.filter((w) => w.id !== workoutId);
    localStorage.setItem(OFFLINE_WORKOUTS_KEY, JSON.stringify(filtered));
  }

  // Clear all stored workouts
  static clearAllWorkouts(): void {
    localStorage.removeItem(OFFLINE_WORKOUTS_KEY);
  }

  // Get workout count
  static getWorkoutCount(): number {
    return this.getStoredWorkouts().length;
  }

  // Check if we're online
  static isOnline(): boolean {
    return navigator.onLine;
  }

  // Setup online/offline event listeners
  static setupConnectivityListeners(
    onOnline: () => void,
    onOffline: () => void
  ): () => void {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Return cleanup function
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }
}

export type { OfflineWorkout };
